// Money helpers for the budget screens: parsing, Indian-style display and validation.

const formatters = new Map();

// Intl formatters are costly to build, so keep one per digit configuration.
const getFormatter = (minimumFractionDigits, maximumFractionDigits) => {
  const key = `${minimumFractionDigits}-${maximumFractionDigits}`;
  if (!formatters.has(key)) {
    formatters.set(key, new Intl.NumberFormat("en-IN", { minimumFractionDigits, maximumFractionDigits }));
  }
  return formatters.get(key);
};

const NUMERIC = /^-?(\d+\.?\d*|\.\d+)$/;

// Number from a number or a string such as "2,00,000.00" or "₹5,000". NaN when the
// value is not an amount ("No Data", "—", "12.5%", ...).
export const parseAmount = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : NaN;
  if (typeof value !== "string") return NaN;

  const cleaned = value.replace(/[₹,\s]/g, "");
  return NUMERIC.test(cleaned) ? Number(cleaned) : NaN;
};

/**
 * "₹2,00,000.00" - rupee sign and lakh/crore grouping.
 *
 * Safe to call on anything a budget table holds: numbers and numeric strings are
 * formatted (already-formatted strings come out the same, so it is idempotent), while
 * percentages and labels such as "12.5%" or "No Data" are returned untouched.
 * A missing value shows as zero.
 */
export const formatINR = (value, { minimumFractionDigits = 2, maximumFractionDigits = 2 } = {}) => {
  if (typeof value === "string" && (value.includes("%") || Number.isNaN(parseAmount(value)))) {
    return value;
  }

  const amount = value === null || value === undefined ? 0 : parseAmount(value);
  if (Number.isNaN(amount)) return String(value);

  const body = getFormatter(minimumFractionDigits, maximumFractionDigits).format(Math.abs(amount));
  const isZero = Number(body.replace(/,/g, "")) === 0; // never "-₹0.00"
  return `${amount < 0 && !isZero ? "-" : ""}₹${body}`;
};

// For optional number fields (Budgeted Hours, Total Budget): leaving the field empty is
// fine - that just means "no value" - but anything typed must be above zero.
// Returns an error message, or null when the value is acceptable.
export const validateOptionalPositive = (value, label = "Value") => {
  if (value === "" || value === null || value === undefined) return null;
  return parseAmount(value) > 0 ? null : `${label} must be greater than 0`;
};

// Same, but the field is required: empty is an error too.
export const validateRequiredPositive = (value, label = "Value") =>
  value === "" || value === null || value === undefined
    ? `${label} is required`
    : validateOptionalPositive(value, label);

// react-hook-form rule for a required, above-zero number:
//   register("TotalBudget", { validate: positiveNumberRule("Total Budget") })
// (validate must return true when the value is fine, hence the `?? true`.)
export const positiveNumberRule = (label) => (value) => validateRequiredPositive(value, label) ?? true;

// react-hook-form `validate` rule for amount inputs: money must be more than zero.
// ("required" and digits-only are separate rules; "0", "00" and "000" all fail here.)
export const validatePositiveAmount = (value) =>
  parseAmount(value) > 0 || "Amount must be greater than 0";
